import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button, CircularProgress, LinearProgress } from '@mui/material';

import './JobProgress.css';
import socket from '../../socket';
import { ApiUrl } from '../../store/config';
import { getSinglePageData } from '../../store/SinglePage';
import { addJob, jobProgress, jobCompleted, jobFailed, jobCancelled, jobStatus, dismissJob } from '../../store/Jobs/index';

export default function JobProgressPanel() {
  const dispatch = useDispatch();
  const activeJobs = useSelector(s => s['Jobs']?.['activeJobs'] || []);
  const currentWorkspaceId = useSelector(s => s['Settings']?.['currentWorkspace']?.['_id']);
  const activeJobsRef = useRef(activeJobs);
  activeJobsRef['current'] = activeJobs;

  const panelRef = useRef(null);
  const dragState = useRef(null);
  const [pos, setPos] = useState({ right: 24, bottom: 24 });
  const [detail, setDetail] = useState({ jobId: null, data: null, loading: false, error: null });
  const [confirmCancel, setConfirmCancel] = useState(null); // jobId pending confirmation, or null

  const fetchJobDetail = async (jobId) => {
    if (!jobId) return;
    setDetail(d => ({ ...d, jobId, loading: true, error: null }));
    try {
      const res = await axios.get(`${ApiUrl}/jobs/${jobId}`);
      setDetail(d => ({ ...d, data: res?.data?.body || res?.data || null, loading: false }));
    } catch (e) {
      setDetail(d => ({ ...d, error: e?.response?.data?.message || 'Failed to load job details', loading: false }));
    }
  };

  const requestCancel = (jobId, e) => {
    if (e) e.stopPropagation();
    if (jobId) setConfirmCancel(String(jobId));
  };

  const confirmCancelJob = async () => {
    const jobId = confirmCancel;
    setConfirmCancel(null);
    if (!jobId) return;
    try {
      await axios.post(`${ApiUrl}/jobs/${jobId}/cancel`);
      dispatch(jobCancelled({ jobId: String(jobId) }));
      if (detail.jobId === jobId) fetchJobDetail(jobId);
    } catch (err) {
      console.error('cancel job failed', err?.response?.data?.message || err.message);
    }
  };

  // Re-register with workspaceId so server can emit job:active for this workspace
  useEffect(() => {
    if (!currentWorkspaceId) return;
    const userId = (() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload._id || payload.id || payload.userId;
      } catch (_) { return null; }
    })();
    if (userId) socket.emit('register', { userId, workspaceId: String(currentWorkspaceId) });
  }, [currentWorkspaceId]);

  useEffect(() => {
    const onActive = ({ jobs }) => {
      jobs.forEach(job => {
        // Reason: server emits Mongo's `_id`. The previous code read `id` which is undefined, so progress events with the correct jobId never matched and the bar stayed at 0%.
        const jid = String(job['_id'] || job['id']);
        dispatch(addJob({ jobId: jid, type: job['type'], total: job['total'], meta: job['meta'] || {} }));
        if (job['progress'] > 0) dispatch(jobProgress({ jobId: jid, progress: job['progress'] }));
        // Backfill the extended status fields so the panel can show step / counts / lastError without waiting for the next socket tick.
        const extras = {};
        ['step', 'scrapesBlocked', 'llmFailures', 'lastError'].forEach(k => { if (job[k] !== undefined) extras[k] = job[k]; });
        if (Object.keys(extras).length > 0) dispatch(jobStatus({ jobId: jid, ...extras }));
      });
    };
    socket.on('job:active', onActive);
    return () => socket.off('job:active', onActive);
  }, [dispatch]);

  useEffect(() => {
    const onProgress = ({ jobId, progress, processed, total }) => {
      dispatch(jobProgress({ jobId: String(jobId), progress, processed, total }));
      // Reason: the dialog is a one-shot REST snapshot; patch it with the same tick the panel gets so both never drift.
      setDetail(d => d['jobId'] === String(jobId) && d['data'] ? { ...d, data: { ...d['data'], progress, processed, total } } : d);
    };
    const onCompleted = ({ jobId, type }) => {
      const jid = String(jobId);
      dispatch(jobCompleted({ jobId: jid }));
      const job = activeJobsRef.current.find(j => j['jobId'] === jid);
      if (type === 'AI Variables') {
        const { campaignId } = job?.['meta'] || {};
        if (campaignId) dispatch({ type: 'GET_AI_VARIABLE_COLUMNS', payload: { campaignId } });
        window.location.reload();
      } else if (type === 'Email Verification') {
        const { viewId, objectType } = job?.['meta'] || {};
        if (objectType) dispatch({ type: 'GET_OBJECTS', payload: { objectType, viewId } });
      } else if (type === 'LinkedIn Enrichment') {
        const { contactId } = job?.['meta'] || {};
        if (contactId) dispatch(getSinglePageData({ objectType: 'contacts', id: contactId }));
        dispatch({ type: 'GET_OBJECTS', payload: { objectType: 'contacts' } });
      }
    };
    const onFailed = ({ jobId, error }) => dispatch(jobFailed({ jobId: String(jobId), error }));
    const onCancelledEvt = ({ jobId }) => dispatch(jobCancelled({ jobId: String(jobId) }));
    const onStatusEvt = ({ jobId, ...rest }) => {
      dispatch(jobStatus({ jobId: String(jobId), ...rest }));
      setDetail(d => d['jobId'] === String(jobId) && d['data'] ? { ...d, data: { ...d['data'], ...rest } } : d);
    };

    socket.on('job:failed', onFailed);
    socket.on('job:progress', onProgress);
    socket.on('job:completed', onCompleted);
    socket.on('job:cancelled', onCancelledEvt);
    socket.on('job:status', onStatusEvt);
    return () => {
      socket.off('job:failed', onFailed);
      socket.off('job:progress', onProgress);
      socket.off('job:completed', onCompleted);
      socket.off('job:cancelled', onCancelledEvt);
      socket.off('job:status', onStatusEvt);
    }
  }, [dispatch]);

  useEffect(() => {
    const timers = activeJobs.filter(job => ['completed', 'failed', 'cancelled'].includes(job.status)).map(job => setTimeout(() => dispatch(dismissJob({ jobId: job.jobId })), 4000));
    return () => timers.forEach(clearTimeout);
  }, [activeJobs, dispatch]);

  const onMouseDown = (e) => {
    if (e.target.closest('.job-progress-panel__dismiss') || e.target.closest('.job-progress-panel__cancel')) return;
    dragState['current'] = {
      startX: e['clientX'],
      startY: e['clientY'],
      startRight: pos['right'],
      startBottom: pos['bottom'],
    };
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!dragState['current']) return;
    const dx = e['clientX'] - dragState['current']['startX'];
    const dy = e['clientY'] - dragState['current']['startY'];
    setPos({
      right: Math.max(0, dragState['current']['startRight'] - dx),
      bottom: Math.max(0, dragState['current']['startBottom'] - dy),
    });
  };

  const onMouseUp = () => {
    dragState['current'] = null;
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);
  };

  if (activeJobs.length === 0) return null;

  return (
    <div
      ref={panelRef}
      onMouseDown={onMouseDown}
      className="job-progress-panel"
      style={{ right: pos['right'], bottom: pos['bottom'] }}
    >
      <div className="job-progress-panel__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="job-progress-panel__title">Background Tasks</span>
          <span className="job-progress-panel__count">{activeJobs.length}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onMouseDown={(e) => e.stopPropagation()}>
          {(() => {
            const running = activeJobs.filter(j => ['running', 'pending'].includes(j.status));
            if (running.length === 0) return null;
            return <button className="job-progress-panel__cancel" onClick={(e) => requestCancel(running[0].jobId, e)} title={running.length > 1 ? `Cancel ${running[0].type}` : 'Cancel job'}>Cancel</button>;
          })()}
          <button
            className="job-progress-panel__dismiss"
            onClick={(e) => { e.stopPropagation(); activeJobs.forEach(j => dispatch(dismissJob({ jobId: j.jobId }))); }}
            title="Hide panel"
          >×</button>
        </div>
      </div>

      {activeJobs.map(job => (
        <div
          key={job['jobId']}
          style={{ cursor: 'pointer' }}
          title="Click to see job details"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => fetchJobDetail(job['jobId'])}
          className={`job-progress-panel__card job-progress-panel__card--${job['status']}`}
        >
          <div className="job-progress-panel__card-top">
            <div className="job-progress-panel__card-label">
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job['type']}</span>
                {job?.meta?.columnName && (
                  <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.meta.columnName}{job.meta.model ? ` · ${job.meta.model}` : ''}
                  </span>
                )}
              </div>
            </div>
            <div className="job-progress-panel__card-right">
              {job['status'] === 'completed' && <span className="job-progress-panel__icon job-progress-panel__icon--done">✓</span>}
              {job['status'] === 'failed' && <span className="job-progress-panel__icon job-progress-panel__icon--fail">✕</span>}
              {job['status'] === 'cancelled' && <span className="job-progress-panel__icon job-progress-panel__icon--fail">⊘</span>}
              {(job['status'] === 'running' || job['status'] === 'pending') && (
                <span className={`job-progress-panel__card-pct ${job['status'] === 'running' ? 'job-progress-panel__card-pct--live' : ''}`}>{job['progress'] || 0}%</span>
              )}
            </div>
          </div>

          {job['status'] === 'running' && (
            <>
              <div className={`job-progress-panel__bar-track ${!job['progress'] ? 'job-progress-panel__bar-track--indeterminate' : ''}`}><div className="job-progress-panel__bar-fill" style={{ width: `${job['progress'] || 0}%` }} /></div>
              {job['step'] && <p className="job-progress-panel__step">{job['step']}</p>}
              {(job['scrapesBlocked'] > 0 || job['llmFailures'] > 0) && (
                <p className="job-progress-panel__warnings">
                  {job['scrapesBlocked'] > 0 && <span>⚠ {job['scrapesBlocked']} site{job['scrapesBlocked'] === 1 ? '' : 's'} blocked</span>}
                  {job['scrapesBlocked'] > 0 && job['llmFailures'] > 0 && <span>  ·  </span>}
                  {job['llmFailures'] > 0 && <span>⚠ {job['llmFailures']} contact{job['llmFailures'] === 1 ? '' : 's'} failed</span>}
                </p>
              )}
              {job['lastError'] && <p className="job-progress-panel__last-error" title={job['lastError']}>{job['lastError']}</p>}
            </>
          )}
          {job['status'] === 'completed' && <p className="job-progress-panel__status-text job-progress-panel__status-text--done">Completed</p>}
          {job['status'] === 'failed' && <p className="job-progress-panel__status-text job-progress-panel__status-text--fail">{job.error || 'Job failed'}</p>}
          {job['status'] === 'cancelled' && <p className="job-progress-panel__status-text job-progress-panel__status-text--fail">Cancelled</p>}
        </div>
      ))}

      <Dialog
        maxWidth="sm" fullWidth
        open={Boolean(detail.jobId)}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        onClose={() => setDetail({ jobId: null, data: null, loading: false, error: null })}
      >
        <DialogTitle sx={{ display: 'flex', marginBottom: '20px', alignItems: 'center', justifyContent: 'space-between', pr: 1.5, py: 2, background: 'linear-gradient(180deg, #fafafa 0%, #f4f4f5 100%)', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)', color: 'var(--primary-color, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✦</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Job details</span>
              {detail.data && <span style={{ fontSize: 11, color: '#64748b' }}>{detail.data.type}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {detail.data && ['pending', 'running'].includes(detail.data.status) && (
              <button className="job-progress-panel__cancel" onClick={() => requestCancel(detail.data._id)} style={{ marginRight: 6 }}>Cancel job</button>
            )}
            <IconButton size="small" onClick={() => fetchJobDetail(detail.jobId)} disabled={detail.loading} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => setDetail({ jobId: null, data: null, loading: false, error: null })} title="Close"><CloseIcon fontSize="small" /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ pt: 3.5, pb: 2.5 }}>
          {detail.loading && !detail.data ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><CircularProgress size={28} /></div>
          ) : detail.error ? (
            <div style={{ color: '#dc2626', fontSize: 13 }}>{detail.error}</div>
          ) : detail.data ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
              <Row label="Status" value={<span className={`job-status-pill job-status-pill--${detail.data.status}`}>{detail.data.status}</span>} />
              {detail.data.step && <Row label="Step" value={detail.data.step} />}
              <Row label="Progress" value={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={detail.data.progress || 0}
                    sx={{ flex: 1, height: 8, borderRadius: 999, backgroundColor: '#f1f5f9', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, var(--primary-color, #6366f1) 0%, #a78bfa 100%)', borderRadius: 999 } }}
                  />
                  <span style={{ minWidth: 90, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#475569', fontWeight: 500 }}>{detail.data.processed || 0} / {detail.data.total || 0} ({detail.data.progress || 0}%)</span>
                </div>
              } />
              {(detail.data.scrapesBlocked > 0 || detail.data.llmFailures > 0) && (
                <Row label="Issues" value={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, color: '#b45309', fontSize: 12 }}>
                    {detail.data.scrapesBlocked > 0 && <span>⚠ {detail.data.scrapesBlocked} website{detail.data.scrapesBlocked === 1 ? '' : 's'} blocked the scraper</span>}
                    {detail.data.llmFailures > 0 && <span>⚠ {detail.data.llmFailures} contact{detail.data.llmFailures === 1 ? '' : 's'} returned no AI value</span>}
                  </div>
                } />
              )}
              {detail.data.lastError && <Row label="Last warning" value={<span style={{ color: '#dc2626', fontSize: 12 }}>{detail.data.lastError}</span>} />}
              {detail.data.startedAt && <Row label="Started" value={new Date(detail.data.startedAt).toLocaleString()} />}
              {detail.data.completedAt && <Row label="Completed" value={new Date(detail.data.completedAt).toLocaleString()} />}
              {detail.data.error && <Row label="Error" value={<span style={{ color: '#dc2626' }}>{detail.data.error}</span>} />}
              <Row label="Job ID" value={<code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#475569' }}>{detail.data._id}</code>} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmCancel} onClose={() => setConfirmCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Cancel this job?</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
            Stop processing the remaining contacts. Values already written to contacts will be kept, and you won't be charged credits for the interrupted run.
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCancel(null)} sx={{ textTransform: 'none' }}>Keep running</Button>
          <Button onClick={confirmCancelJob} variant="contained" color="error" sx={{ textTransform: 'none' }}>Cancel job</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, paddingBottom: 8, borderBottom: '1px dashed #f1f5f9' }}>
    <span style={{ minWidth: 100, color: '#64748b', fontWeight: 500, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04 }}>{label}</span>
    <span style={{ flex: 1, color: '#0f172a', wordBreak: 'break-word' }}>{value ?? '—'}</span>
  </div>
);
